<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useLoginSection, type LoginSectionProps, type SmartbookGrantContext } from './useLoginSection'
import { apiPost, httpPost } from '@/lib/http'

const props = defineProps<LoginSectionProps & { context: SmartbookGrantContext }>()

const emit = defineEmits<{
  (e: 'change', section: string): void
  (e: 'update:shared-data', data: Record<string, unknown>): void
}>()

const { changeSection, setDataItem, onAuthSuccess } = useLoginSection(props, emit)

const { notify } = useNotification()

const progress = ref(false)
const errors = ref<Record<string, string>>({})
const fields = reactive({
  last_name: '',
  name: '',
  patronymic: '',
  birthdate: '',
  gender: ''
})
const genders = [
  { text: 'Не указано', value: '-' },
  { text: 'Мальчик', value: 'm' },
  { text: 'Девочка', value: 'f' }
]

async function submit() {
  if (progress.value) return
  progress.value = true
  errors.value = {}

  try {
    const resp = await httpPost<{
      failed?: boolean
      message?: string
      errors?: Record<string, Array<{ message: string }>>
      data?: { id: number; name: string; last_name: string }
    }>('/parent/request_add_student', {
      ...fields,
      birthdate: fields.birthdate.split('.').reverse().join('-')
    })

    if (resp.failed && resp.errors) {
      for (const key in resp.errors) {
        errors.value[key] = resp.errors[key][0].message
      }
    } else if (resp.failed) {
      throw resp.message
    } else if (props.query.get('purpose') === 'smartbook') {
      const students = props.sharedData.smartbookStudents || []
      if (resp.data) {
        students.push({ id: resp.data.id, first_name: resp.data.name, last_name: resp.data.last_name })
      }
      setDataItem('smartbookStudents', students)

      if (props.sharedData.parentHasStudents) {
        changeSection(props.LoginSection.SmartbookStudentSelect)
      } else {
        changeSection(props.LoginSection.SmartbookPurchase)
      }
    } else if (props.query.get('purpose') === 'smartbook_grant') {
      if (resp.data) {
        await confirmSmartbookGrant(resp.data.id)
      }
    } else {
      onAuthSuccess()
    }
  } catch (err) {
    let title = 'Произошла неизвестная ошибка при добавлении ученика. Повторите попытку позже'
    if (typeof err === 'string') title = err
    notify({ title })
  }

  progress.value = false
}

async function confirmSmartbookGrant(studentId: number) {
  if (!props.context.smartbook) return

  try {
    const resp = await apiPost('/teacher/request_parent_confirm_smartbook_grant', {
      grant_id: props.context.smartbook.grant_id,
      student_id: studentId
    })
    if (resp.failed) throw resp.message
    sessionStorage.showParentOnboarding = 'true'
    onAuthSuccess()
  } catch {
    window.location.href = '/parent/cabinet'
  }
}
</script>

<template>
  <div class="login-smartbook-student-create">
    <v-notifications position="bottom right"></v-notifications>
    <h2 class="text-h3">Добавить ученика</h2>

    <v-field class="full-width mt-3" label="Фамилия">
      <v-input v-model="fields.last_name" placeholder="Введите фамилию" :error="errors.last_name" />
    </v-field>
    <v-field class="full-width mt-3" label="Имя">
      <v-input v-model="fields.name" placeholder="Введите имя" :error="errors.name" />
    </v-field>
    <v-field class="full-width mt-3">
      <template #label>
        <p class="d-flex justify-space-between align-center">
          Отчество
          <span class="text-caption gray-text">Необязательно</span>
        </p>
      </template>
      <v-input v-model="fields.patronymic" placeholder="Введите отчество" :error="errors.patronymic" />
    </v-field>
    <v-field class="full-width mt-3" label="Дата рождения">
      <v-input-date v-model="fields.birthdate" placeholder="ДД.ММ.ГГГГ" :error="errors.birthdate" />
    </v-field>
    <v-field class="full-width mt-3" label="Пол">
      <v-select v-model="fields.gender" placeholder="Выберите" :error="errors.gender" :items="genders" />
    </v-field>

    <template v-if="sharedData.parentHasStudents">
      <v-button
        class="mt-3"
        mode="outlined large block"
        @click="() => changeSection(LoginSection.SmartbookStudentSelect)"
      >
        Назад
      </v-button>

      <v-button class="mt-3" mode="primary filled large block" @click="submit"> Добавить </v-button>
    </template>

    <v-button v-else-if="query.get('purpose') === 'smartbook'" class="mt-3" mode="primary filled block" @click="submit">
      Добавить и перейти к оплате
    </v-button>

    <v-button
      v-else-if="query.get('purpose') === 'smartbook_grant'"
      class="mt-3"
      mode="primary filled block"
      @click="submit"
    >
      Добавить ученика и получить УУ
    </v-button>

    <template v-else>
      <v-button class="mt-3" mode="outlined large block" @click="onAuthSuccess"> Пропустить </v-button>

      <v-button class="mt-3" mode="primary filled large block" @click="submit"> Добавить </v-button>
    </template>
  </div>
</template>

<style lang="scss">
.login-smartbook-student-create {
  display: flex;
  flex-direction: column;
  align-items: center;

  @include desktop {
    width: 320px !important;
    margin: 0 auto;
  }

  & .mx-datepicker {
    width: 100%;
  }

  .v-field__label {
    margin-right: 0;
  }
}
</style>
